import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Iconify from "~src/components/Iconify";
import BrandAvatar from "~src/components/BrandAvatar";
import BrandCover from "~src/components/BrandCover";
import { Brand } from "~src/types/brand";
import { MobileMenu } from "./MobileMenu";
import { useState } from "react";
import { featureBrand, trackBrand } from "~src/api/brands";
import useAuth from "~src/hooks/useAuth";
import useGetFullBrand from "~src/hooks/useGetFullBrand";
import useTabs from "../../hooks/useTabs";
import {logError} from "~src/utils/logger"

export const BrandHeader = ({ 
  brand, 
  fullBrandLoadingContext 
}: { 
  brand: Brand;
  fullBrandLoadingContext?: ReturnType<typeof useGetFullBrand>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack>
      <BrandCover
        brandName={brand.brandname}
        coverImage={brand.coverimage}
        height="240px"
        sx={{
          width: "100%",
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mx: 3,
          minHeight: "142px", // Add minHeight to prevent layout shift
        }}
      >
        <Stack direction="row" spacing={3}>
          <BrandLogo brand={brand} />
          <Stack sx={{ pt: 3 }}>
            <Typography variant="h3">{brand.brandname}</Typography>
            <Chip
              label={brand.category_name}
              sx={{
                maxWidth: {
                  xs: "164px",
                  sm: "none",
                },
              }}
            />
          </Stack>
        </Stack>
        {!isMobile && <HeaderButtons brand={brand} fullBrandLoadingContext={fullBrandLoadingContext} />}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          minHeight: isMobile ? "42px" : "0px", // Add minHeight for mobile buttons
        }}
      >
        {isMobile && <HeaderButtons brand={brand} fullBrandLoadingContext={fullBrandLoadingContext} />}
      </Box>
    </Stack>
  );
};

const BrandLogo = ({ brand }: { brand: Brand }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bottom: 42,
        position: "relative",
      }}
    >
      <Box
        sx={{
          borderRadius: "100%",
          width: 172,
          height: 172,
          backgroundColor: theme.palette.grey[50],
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: theme.spacing(2),
        }}
      >
        <BrandAvatar
          brandName={brand.brandname}
          profileImage={brand.profileimage}
          size={160}
          sx={{
            border: `1px solid ${theme.palette.grey[300]}`,
          }}
        />
      </Box>
    </Box>
  );
};

const HeaderButtons = ({
  brand,
  fullBrandLoadingContext
}: {
  brand: Brand;
  fullBrandLoadingContext?: ReturnType<typeof useGetFullBrand>;
}) => {
  // Use the passed context - parent should always provide it
  const fullBrand = fullBrandLoadingContext?.fullBrand;

  const navigate = useNavigate();
  const { onChangeTab } = useTabs();
  const { user } = useAuth();
  const is_report_published = fullBrand?.report_published;
  const is_superuser = user?.is_superuser;

  const [isFeatured, setIsFeatured] = useState<number | undefined>(fullBrand?.is_featured);
  const [prevIsFeatured, setPrevIsFeatured] = useState(fullBrand?.is_featured);

  // Sync state from prop during render (not in useEffect) to avoid extra render cycle
  if (fullBrand?.is_featured !== prevIsFeatured) {
    setPrevIsFeatured(fullBrand?.is_featured);
    if (fullBrand?.is_featured !== undefined) {
      setIsFeatured(fullBrand?.is_featured);
    }
  }

  const handleFeatureBrand = async () => {
    const payload = isFeatured === 0 ? 1 : 0;
    await featureBrand(brand.brand_id, payload)
      .then(() => {
        setIsFeatured(isFeatured === 0 ? 1 : 0);
      })
      .catch((error) => logError(error));
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        pt: 3,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: {
            sm: "inline-flex",
            md: "none",
          },
        }}
      >
        <MobileMenu brand={brand} />
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          display: {
            xs: "none",
            md: "inline-flex",
          },
        }}
      >
        {is_superuser && fullBrand && !is_report_published && (
          <Button
            onClick={(e) => {
              onChangeTab(e, "trend-viability");
              navigate("trend-viability");
            }}
            variant="outlined"
            startIcon={<Iconify icon="carbon:report" />}
          >
            Create Report
          </Button>
        )}
        {is_superuser && fullBrand && (
          <Button
            onClick={handleFeatureBrand}
            variant="outlined"
            startIcon={
              <Iconify
                icon={
                  isFeatured
                    ? "material-symbols:star"
                    : "material-symbols:star-outline"
                }
                sx={{ color: isFeatured ? "#ff9800" : "inherit" }}
              />
            }
          >
            {isFeatured ? "Featured" : "Feature Brand"}
          </Button>
        )}
        <TrackButton brand={brand} />
        {brand.brandsite && (
          <Button
            onClick={() => window.open(brand.brandsite, "_blank")}
            variant="outlined"
            startIcon={<Iconify icon="tabler:external-link" />}
          >
            Website
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const TrackButton = ({ brand }: { brand: Brand }) => {
  const [tracked, setTracked] = useState(brand.tracked || false);

  const handleClick = () => {
    setTracked(!tracked);
    trackBrand(brand.brand_id, !tracked);
  };

  return (
    <Button
      variant="outlined"
      startIcon={
        <Iconify
          icon={tracked ? "ph:heart-fill" : "ph:heart"}
          color={tracked ? "red" : undefined}
        />
      }
      onClick={handleClick}
    >
      {tracked ? "Tracked" : " Track"}
    </Button>
  );
};
